import React, { Fragment } from 'react';
import { Box, Typography, Link, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  isStructuredSourceDoc,
  pagesToLinkRanges,
  pdfUrlWithPage,
} from '../utils/sourceDocuments';

/**
 * @param {object} props
 * @param {unknown[]} props.items — normalized `source_documents` list
 * @param {'panel' | 'compact'} [props.variant='panel'] — Right panel vs list expando
 * @param {object} [props.colors] — RightPanel `colors` when variant=panel
 */
export default function SourceDocumentsList({ items, variant = 'panel', colors }) {
  const structured = (items || []).filter(isStructuredSourceDoc);
  if (!structured.length) return null;

  const isPanel = variant === 'panel';
  const linkSx = isPanel
    ? {
        fontWeight: 600,
        color: colors?.primary || '#7454A5',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline', color: colors?.primaryHover || '#5e4385' },
      }
    : {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#7454A5',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline', color: '#5a3d80' },
      };

  const prefixSx = isPanel
    ? { fontSize: '0.875rem', color: colors?.textSecondary || '#6b7280' }
    : { fontSize: '0.75rem', color: '#666' };

  const rowBoxSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: isPanel ? 1 : 0.75,
    p: isPanel ? 1.5 : 1,
    border: `1px solid ${colors?.border || '#e5e7eb'}`,
    borderRadius: 2,
    bgcolor: colors?.surface || '#f8f9fa',
  };

  const buttonSx = isPanel
    ? {
        flexShrink: 0,
        textTransform: 'none',
        color: colors?.text || '#111827',
        borderColor: colors?.border || '#e5e7eb',
        borderRadius: 2,
        py: 0.75,
        px: 1.25,
        fontWeight: 600,
        maxWidth: '100%',
        '& .MuiButton-startIcon': { mr: 0.75 },
        '&:hover': { bgcolor: colors?.surface || '#f8f9fa', borderColor: colors?.textSecondary || '#6b7280' },
      }
    : {
        flexShrink: 0,
        textTransform: 'none',
        fontSize: '0.75rem',
        py: 0.25,
        px: 1,
        fontWeight: 600,
        color: '#333',
        borderColor: '#ddd',
        maxWidth: '100%',
        '& .MuiButton-startIcon': { mr: 0.5, '& svg': { fontSize: '0.95rem' } },
      };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isPanel ? 1.5 : 1 }}>
      {structured.map((src, i) => {
        const label = src.label || src.document_id || `File ${i + 1}`;
        const ranges = pagesToLinkRanges(src.pages);
        const baseUrl = src.signed_url;

        return (
          <Box key={src.document_id || src.label || i} sx={rowBoxSx}>
            <Button
              component="a"
              href={baseUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size={isPanel ? 'medium' : 'small'}
              startIcon={<OpenInNewIcon sx={{ color: colors?.textSecondary || '#6b7280', fontSize: isPanel ? '1.1rem' : '1rem' }} />}
              sx={buttonSx}
            >
              {label}
            </Button>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.5, rowGap: 0.25, minWidth: 0, pl: 0.25 }}>
              <Typography component="span" sx={prefixSx}>
                pg{' '}
              </Typography>
              {ranges.length === 0 ? (
                <Link href={baseUrl} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                  open
                </Link>
              ) : (
                ranges.map((r, j) => (
                  <Fragment key={`${r.from}-${r.to}`}>
                    {j > 0 ? (
                      <Typography component="span" sx={prefixSx}>
                        ,{' '}
                      </Typography>
                    ) : null}
                    <Link href={pdfUrlWithPage(baseUrl, r.from)} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                      {r.from === r.to ? r.from : `${r.from}-${r.to}`}
                    </Link>
                  </Fragment>
                ))
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
